/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import classNames from 'classnames';

import Notification from '../../../models/notificationModel';

import {getProductsLabel} from '../../../util/emailStepUtil';

import {ActivateHandle} from '../../../util/interfaces';

import ProductSelectionController from '../../productSelection/productSelectionController';

import {ProductSelection} from '../../../util/types';

import {ResponseData} from '@dvrd/dvr-controls';

import {onShowConfirmSnackbar} from '../../../util/renderUtil';

import {createErrorMessage} from '../../../util/utils';

import {showDialog} from '@dvrd/dvr-controls';

import {BolmateButton} from '../../controls';

interface Props {
    onSuccess: (notification: Notification) => void;
    notification: Notification;
    disabled: boolean;
    className?: string;
    text?: string;
}

export default function NotificationProducts(props: Props) {
    const {t} = useTranslation();
    const {onSuccess, notification, disabled, className, text} = props;
    const productsRef = useRef<ActivateHandle>(null);
    const buttonLabel = useMemo(() => {
        const {productSelectionJSON, productSelection, numProducts} = notification;
        return getProductsLabel(productSelectionJSON.clauses, productSelection, numProducts);
    }, [notification.productSelectionJSON, notification.productSelection, notification.numProducts]);

    function onSubmitProductSelection(selection: ProductSelection) {
        const notif = notification.clone();
        notif.productSelectionJSON = selection;
        // Confirm on "Done" even when nothing changed (no backend write needed)
        // — the user still expects a green "saved" toast for the action.
        if (!notif.productSelectionChanged) {
            onShowConfirmSnackbar(t('notifications.components.productsSavedSnackbar', 'Producten opgeslagen'));
            return productsRef.current?.close!();
        }
        notif.productSelectionJSON = selection;
        notif.update((notification: Notification, success: boolean, data: ResponseData) => {
            if (success) {
                onSuccess(notification);
                onShowConfirmSnackbar(
                    t('notifications.components.notificationUpdatedSnackbar', 'De notificatie is bijgewerkt')
                );
                productsRef.current?.close!();
            } else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t(
                                'notifications.components.notificationUpdateErrorMessage',
                                'Het bijwerken van de notificatie is niet gelukt.'
                            )
                    ),
                    t('notifications.components.notificationUpdateErrorTitle', 'Bijwerken mislukt')
                );
        });
    }

    function onOpenProducts() {
        const selection = notification.productSelectionJSON;
        productsRef.current?.activate(selection);
    }

    return (
        <>
            <section>
                <label className='fixed-text'>{text ?? t('notifications.components.productsForLabel', 'voor')}</label>
                <BolmateButton
                    outlined
                    size='normal'
                    label={buttonLabel}
                    onClick={onOpenProducts}
                    disabled={disabled}
                    className={classNames('products-button', className)}
                />
            </section>
            <ProductSelectionController
                loading={false}
                onSubmit={onSubmitProductSelection}
                ref={productsRef}
                // "Remaining" means something else here than in the e-mail
                // flows: products not covered by another notification of this
                // type (see select_notification_remaining_products).
                remainingInfoText={t(
                    'notifications.components.selectRemainingInfo',
                    'Met deze optie worden automatisch alle producten geselecteerd die nog niet onder een andere notificatie van dit type vallen.'
                )}
            />
        </>
    );
}
